import { Data, Model } from '@singularsystems/neo-core';
import { AxiosPromise } from 'axios';
import { injectable } from 'inversify';
import { AppService, Types } from '../DomainTypes';
import FieldConfigValues from '../Models/Config/FieldConfig/FieldConfigValues';
import { ConfigValues } from '../Models/Config/TenantConfig';
import CorrespondenceConfigValues from '../Models/Config/CorrespondenceConfig';
import { ExportConfig } from '../Models/Config/ExportConfig';
import { ReportingConfigValues } from "../Models/Config/ReportingConfig";

export interface IConfigApiClient {

    /**
     * Gets the tenant's config.
     * @returns The system config.
     */
    getConfig(): AxiosPromise<Model.PlainTrackedObject<ConfigValues>>;

    /**
     * Updates the tenant's config.
     * @param command The config update command.
     * @returns A  representing the asynchronous update operation.
     */
    updateConfig(command: Model.PartialPlainObject<ConfigValues>): AxiosPromise<Model.PlainObject<ConfigValues>>;

    /**
     * Gets the tenant's reporting config.
     * @returns The Reporting config.
     */
    getReportingConfig(): AxiosPromise<Model.PlainTrackedObject<ReportingConfigValues>>;

    /**
     * Updates the tenant's reporting config.
     * @param command The reporting config update command.
     * @returns A  representing the asynchronous update operation.
     */
    updateReportingConfig(command: Model.PartialPlainObject<ReportingConfigValues>): AxiosPromise;

    /**
     * Gets the tenant's config.
     * @returns The field config.
     */
    getFieldConfig(): AxiosPromise<Model.PlainTrackedObject<FieldConfigValues>>;

    /**
     * Updates the tenant's config.
     * @param command The config update command.
     * @returns A Task representing the asynchronous update operation.
     */
    updateFieldConfig(command: Model.PartialPlainObject<FieldConfigValues>): AxiosPromise;

    /**
     * Gets the correspondence config.
     */
    getCorrespondenceConfig(): AxiosPromise<Model.PlainObject<CorrespondenceConfigValues>>;

    /**
     * Updates the correspondence config.
     */
    updateCorrespondenceConfig(correspondenceConfig: Model.PartialPlainObject<CorrespondenceConfigValues>): AxiosPromise;

    /** 
     * Gets the export config for the current tenant.
     */
    getExportConfig(): AxiosPromise<Model.PlainObject<ExportConfig>>;

    /** 
     * Updates the export config for the current tenant.
     */
    updateExportConfig(exportConfig: Model.PartialPlainObject<ExportConfig>): AxiosPromise;

    // Client only properties / methods
}

@injectable()
export default class ConfigApiClient extends Data.ApiClientBase implements IConfigApiClient {

    constructor (config = AppService.get(Types.App.Config)) {
        super(`${config.apiPath}/config`);
    }

    public getConfig(): AxiosPromise<Model.PlainTrackedObject<ConfigValues>> {
        return this.axios.get(`${this.apiPath}/config`);
    }

    public updateConfig(command: Model.PartialPlainObject<ConfigValues>): AxiosPromise<Model.PlainObject<ConfigValues>> {
        return this.axios.post(`${this.apiPath}/config`, command);
    }

    public getReportingConfig(): AxiosPromise<Model.PlainTrackedObject<ReportingConfigValues>> {
        return this.axios.get(`${this.apiPath}/reporting-config`);
    }
    
    public updateReportingConfig(command: Model.PartialPlainObject<ReportingConfigValues>): AxiosPromise {
        return this.axios.post(`${this.apiPath}/reporting-config`, command);
    }

    public getFieldConfig(): AxiosPromise<Model.PlainTrackedObject<FieldConfigValues>> {
        return this.axios.get(`${this.apiPath}/field-config`);
    }

    public updateFieldConfig(command: Model.PartialPlainObject<FieldConfigValues>): AxiosPromise {
        return this.axios.post(`${this.apiPath}/field-config`, command);
    }

    public getCorrespondenceConfig(): AxiosPromise<Model.PlainObject<CorrespondenceConfigValues>> {
        return this.axios.get(`${this.apiPath}/correspondence`);
    }

    public updateCorrespondenceConfig(correspondenceConfig: Model.PartialPlainObject<CorrespondenceConfigValues>): AxiosPromise {
        return this.axios.post(`${this.apiPath}/correspondence`, correspondenceConfig);
    }

    public getExportConfig(): AxiosPromise<Model.PlainObject<ExportConfig>> {
        return this.axios.get(`${this.apiPath}/export`);
    }

    public updateExportConfig(exportConfig: Model.PartialPlainObject<ExportConfig>): AxiosPromise {
        return this.axios.post(`${this.apiPath}/export`, exportConfig);
    }

    // Client only properties / methods
}